/**
 * Trade economics as ONE editable card (ECONOMICS_LAYOUT = "single").
 *
 * Inputs the loan STORES are prefilled with their saved values and edited in
 * place; results are read-only rows beside them. "Recompute & save" (page
 * header) always sends every prefilled input, not just the edited ones — the
 * save routes do NOT fall back to the stored buy rate (and fall back to Term
 * Months, not the stored strip term), so a blank would silently re-price the
 * strip (eastview-os app/routers/loan_acquisitions.py rtl_buy_rate /
 * dscr_bond_points).
 *
 * RTL results preview live: the math is the backend's own
 * (acquisition_economics.rtl_buy_rate_economics → pricing.RTLLock):
 *   buy rate     = override ?? base + spread + risk adj.
 *   strip rate   = coupon − buy rate
 *   strip / year = strip rate × Current UPB
 *   strip value  = strip / year × term / 12
 *   price        = Current UPB (at par) · wire = price + accrued interest
 * DSCR prices through DSCRLock (LLPAs etc.), so its results are NOT previewed
 * — they show the saved values, italic, until the save recalculates them.
 */
import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import TrendingUpOutlined from "@mui/icons-material/TrendingUpOutlined";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import LoanSectionCard from "../../shared/tabLayout/LoanSectionCard";
import SectionEmptyState from "../../shared/tabLayout/SectionEmptyState";
import { TabGhostButton } from "../../shared/tabLayout/tabButtons";
import { TAB_TOKENS as T } from "../../shared/tabLayout/tabTokens";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import { InputRow, StatRow } from "./economicsRows";

export const BOND_POINTS = "Bond Points";
export const BUY_RATE_STRIP = "Buy Rate + Strip";

const n = (v) => {
  const x = Number(v);
  return v == null || v === "" || !Number.isFinite(x) ? null : x;
};
const pct = (v) => (v == null ? null : formatPercent(v));
const money = (v) =>
  v == null ? null : formatDollar(Math.round(v * 100) / 100, null);
const points = (v) => (v == null ? null : Number(v).toFixed(3));

/* ── the inputs, per convention. `seed` reads the saved value; `stored:false`
      inputs are never saved by the backend, so they start blank and live in
      the collapsed "advanced" block. Keys are the POST body keys. ── */
const FIELDS = {
  [BUY_RATE_STRIP]: [
    {
      key: "coupon_rate",
      label: "Coupon rate",
      kind: "percent",
      group: "Pricing",
      help: "The note rate the borrower pays. Saving it also updates the loan's note rate.",
      seed: (e, l) => e?.couponRate ?? l?.noteRate ?? l?.interestRate,
    },
    {
      key: "buyRate",
      label: "Buy rate",
      kind: "percent",
      group: "Pricing",
      help: "The rate the loan is bought to. Leave the pricing build-up below empty to use this.",
      seed: (e, l) => e?.buyRate ?? l?.buyRate,
    },
    {
      key: "currentUPB",
      label: "Current UPB",
      kind: "money",
      group: "Amounts",
      help: "The unpaid principal balance being purchased.",
      seed: (e, l) => l?.currentUPB ?? e?.currentUPB,
    },
    {
      key: "term_months",
      label: "Strip term (months)",
      kind: "number",
      group: "Amounts",
      help: "How many months the interest strip is valued over.",
      seed: (e, l) =>
        e?.interestStripTermMonths ??
        l?.interestStripTermMonths ??
        l?.termMonths,
    },
    {
      key: "accrued_interest",
      label: "Accrued interest",
      kind: "money",
      group: "Amounts",
      help: "Interest owed from the paid-to date to the cut-off date. It is added to the funding wire.",
      seed: (e, l) => e?.accruedInterest ?? l?.accruedInterest,
    },
    // Never stored — a one-time way to build the buy rate instead of typing it.
    {
      key: "pricing_base_rate",
      label: "Pricing base rate",
      kind: "percent",
      stored: false,
    },
    {
      key: "pricing_spread",
      label: "Pricing spread",
      kind: "percent",
      stored: false,
    },
    {
      key: "risk_spread_adjustment",
      label: "Risk spread adj.",
      kind: "percent",
      stored: false,
    },
  ],
  [BOND_POINTS]: [
    {
      key: "couponRate",
      label: "Coupon rate",
      kind: "percent",
      group: "Pricing",
      help: "The note rate. Saving it also updates the loan's note rate.",
      seed: (e, l) => e?.couponRate ?? l?.noteRate ?? l?.interestRate,
    },
    {
      key: "bondPrice",
      label: "Bond price",
      kind: "number",
      group: "Pricing",
      help: "Price in points, where par is 100.",
      seed: (e, l) => e?.bondPrice ?? l?.bondPrice,
    },
    {
      key: "currentUPB",
      label: "Current UPB",
      kind: "money",
      group: "Amounts",
      help: "The unpaid principal balance being purchased.",
      seed: (e, l) => l?.currentUPB ?? e?.currentUPB,
    },
    {
      key: "accrued_interest",
      label: "Accrued interest",
      kind: "money",
      group: "Amounts",
      help: "Interest owed from the paid-to date to the cut-off date. It is added to the funding wire.",
      seed: (e, l) => e?.accruedInterest ?? l?.accruedInterest,
    },
    {
      key: "margin_points_at_lock",
      label: "Margin points",
      kind: "number",
      stored: false,
      help: "Points added on top of the bond price for this recalculation.",
    },
  ],
};

export const editorFields = (convention) => FIELDS[convention] || [];

/** Saved values for every stored input (null when the loan has none). */
export const editorSeed = (convention, economics, loan) =>
  editorFields(convention).reduce((acc, f) => {
    if (f.stored === false) return acc;
    const v = f.seed?.(economics, loan);
    return { ...acc, [f.key]: v == null || v === "" ? null : v };
  }, {});

const toNum = (v) => {
  if (v === "" || v == null) return null;
  const x = Number(v);
  return Number.isNaN(x) ? v : x;
};

/** The values the card shows: saved values with the user's edits on top. */
export const editorValues = (seed, edits) => ({ ...seed, ...edits });

/** Edits that actually differ from the saved value. */
export const editedKeys = (seed, edits) =>
  Object.keys(edits).filter((k) => toNum(edits[k]) !== toNum(seed[k] ?? null));

/**
 * POST body: EVERY prefilled input (so nothing falls back on the backend),
 * plus any one-time pricing inputs. A pricing base rate replaces the buy
 * rate — the backend prefers `buyRate` when both are sent, so it is dropped.
 */
export const editorPayload = (convention, seed, edits) => {
  const values = editorValues(seed, edits);
  const body = Object.entries(values).reduce((acc, [k, v]) => {
    const x = toNum(v);
    return x == null ? acc : { ...acc, [k]: x };
  }, {});
  if (convention === BUY_RATE_STRIP && body.pricing_base_rate != null) {
    delete body.buyRate;
  }
  return body;
};

/* RTL live preview — the backend's formula (see header). */
const rtlPreview = (v) => {
  const coupon = n(v.coupon_rate);
  const built =
    n(v.pricing_base_rate) != null
      ? n(v.pricing_base_rate) +
        (n(v.pricing_spread) ?? 0) +
        (n(v.risk_spread_adjustment) ?? 0)
      : null;
  const buy = built ?? n(v.buyRate);
  const upb = n(v.currentUPB);
  const strip = coupon != null && buy != null ? coupon - buy : null;
  const annual = strip != null && upb != null ? strip * upb : null;
  const term = n(v.term_months);
  const price = upb;
  return {
    buyFromPricing: built,
    interestStripRate: strip,
    interestStripAnnualAmount: annual,
    interestStripValue:
      annual != null && term != null ? (annual * term) / 12 : null,
    purchasePriceAmount: price,
    fundingWireAmount:
      price != null ? price + (n(v.accrued_interest) ?? 0) : null,
  };
};

const GroupTitle = ({ children, first }) => (
  <Txt
    component="div"
    sx={{
      ...T.type.fieldLabel,
      fontSize: "12.5px",
      color: T.color.ink,
      paddingTop: first ? "2px" : "28px",
      paddingBottom: 0,
    }}
  >
    {children}
  </Txt>
);

GroupTitle.propTypes = { children: PropTypes.node, first: PropTypes.bool };

const EconomicsEditorCard = ({
  convention,
  economics,
  seed,
  edits,
  onEdit,
  onReset,
  saving,
}) => {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const fields = editorFields(convention);
  const values = editorValues(seed, edits);
  const changed = editedKeys(seed, edits);
  const dirty = changed.length > 0;
  const isBond = convention === BOND_POINTS;

  if (!fields.length) {
    return (
      <LoanSectionCard icon={<TrendingUpOutlined />} title="Economics">
        <SectionEmptyState
          icon={<TrendingUpOutlined />}
          message="Set the loan's product type to resolve a trade convention and compute its economics."
        />
      </LoanSectionCard>
    );
  }

  const field = (key) => fields.find((f) => f.key === key);
  const input = (key) => {
    const f = field(key);
    return (
      <InputRow
        key={key}
        field={f}
        value={values[key]}
        edited={changed.includes(key)}
        onChange={(v) => onEdit(key, v)}
      />
    );
  };

  // Results: RTL previews live; DSCR shows the saved values until saved.
  const live = isBond ? null : rtlPreview(values);
  const result = (key) => (live ? live[key] : economics?.[key]);
  const pending = isBond && dirty;
  const resultRow = (label, value, hint) => (
    <StatRow
      key={label}
      label={label}
      value={value}
      hint={hint}
      pending={pending}
    />
  );

  const advanced = fields.filter((f) => f.stored === false);
  const advancedUsed = advanced.some((f) => n(values[f.key]) != null);

  return (
    <LoanSectionCard
      icon={<TrendingUpOutlined />}
      title="Economics"
      subTitle={
        isBond
          ? "Edit the inputs, then Recompute & save to reprice. Results update when saved."
          : "Edit the inputs and the results update as you type. Recompute & save stores them."
      }
      count={dirty ? `${changed.length} changed` : undefined}
      controls={
        <TabGhostButton
          size="sm"
          label="Reset"
          disabled={saving || (!dirty && !advancedUsed)}
          onClick={onReset}
        />
      }
      bodySx={{ paddingBlock: "20px" }}
    >
      <GroupTitle first>Pricing</GroupTitle>
      {isBond ? (
        <>
          {input("couponRate")}
          {input("bondPrice")}
          {resultRow(
            "Premium / discount points",
            points(economics?.premiumDiscountPoints),
            "Bond price minus 100.",
          )}
        </>
      ) : (
        <>
          {input("coupon_rate")}
          {input("buyRate")}
          {live.buyFromPricing != null &&
            resultRow(
              "Buy rate from pricing",
              pct(live.buyFromPricing),
              "Base rate + spread + risk adjustment. Used instead of the buy rate above.",
            )}
          {resultRow(
            "Interest strip rate",
            pct(result("interestStripRate")),
            "Coupon rate minus the buy rate.",
          )}
        </>
      )}

      <GroupTitle>Amounts</GroupTitle>
      {input("currentUPB")}
      {!isBond && input("term_months")}
      {input("accrued_interest")}
      {isBond ? (
        <>
          {resultRow("Premium / discount", money(economics?.premiumDiscount))}
          {resultRow("Purchase price", money(economics?.purchasePriceAmount))}
        </>
      ) : (
        <>
          {resultRow(
            "Strip / year",
            money(result("interestStripAnnualAmount")),
            "Interest strip rate × Current UPB.",
          )}
          {resultRow(
            "Strip value (term)",
            money(result("interestStripValue")),
            "Strip per year × the strip term in years.",
          )}
          {resultRow(
            "Purchase price (at par)",
            money(result("purchasePriceAmount")),
          )}
        </>
      )}

      {/* One-time inputs the backend never saves. */}
      <Box
        component="button"
        type="button"
        onClick={() => setAdvancedOpen((o) => !o)}
        aria-expanded={advancedOpen}
        sx={{
          all: "unset",
          cursor: "pointer",
          mt: "20px",
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          fontSize: 12.5,
          fontWeight: 600,
          color: T.color.accentInk,
          "&:focus-visible": { outline: `2px solid ${T.color.brandSoft}` },
        }}
      >
        <ExpandMoreRounded
          sx={{
            fontSize: 18,
            transition: "transform .15s",
            transform: advancedOpen ? "rotate(180deg)" : "none",
          }}
        />
        {isBond ? "Add margin points" : "Build the buy rate from pricing"}
        {advancedUsed && !advancedOpen && " (in use)"}
      </Box>
      <Collapse in={advancedOpen}>
        <Txt
          component="div"
          sx={{ ...T.type.meta, fontSize: 12, mt: "6px", mb: "2px" }}
        >
          {isBond
            ? "Used for this recalculation only. It is not stored on the loan."
            : "Used for this recalculation only, in place of the buy rate. These are not stored on the loan; the resulting buy rate is."}
        </Txt>
        {advanced.map((f) => input(f.key))}
      </Collapse>

      {/* The bottom line the trade desk wires. */}
      <Flx
        ac
        jb
        sx={{
          marginTop: "20px",
          padding: "12px 16px",
          gap: "14px",
          background: T.color.brandSoft,
          border: "1px solid #d9e0fa",
          borderRadius: T.radius.sm,
        }}
      >
        <Flx column sx={{ gap: "1px", minWidth: 0 }}>
          <Txt sx={{ fontSize: 13, fontWeight: 700, color: T.color.accentInk }}>
            Funding wire amount
          </Txt>
          <Txt
            sx={{
              fontSize: 10.5,
              fontWeight: 500,
              color: T.color.accentInk,
              opacity: 0.8,
            }}
          >
            Purchase price + accrued interest
          </Txt>
        </Flx>
        <Txt
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: T.color.accentInk,
            fontVariantNumeric: "tabular-nums",
            fontStyle: pending ? "italic" : "normal",
            flexShrink: 0,
          }}
        >
          {money(result("fundingWireAmount")) || "—"}
        </Txt>
      </Flx>
    </LoanSectionCard>
  );
};

EconomicsEditorCard.propTypes = {
  convention: PropTypes.string,
  economics: PropTypes.object,
  seed: PropTypes.object.isRequired,
  edits: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  saving: PropTypes.bool,
};

export default EconomicsEditorCard;
